import React, { useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { VALID_GAME_ID } from '@root/constants';
import { useAuth } from '@root/hooks';
import { isRunComplete, summariseRuns, type RunSummary } from '@root/lib';
import { appSelector, AppSliceState } from '@root/store/slices/appSlice';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import {
  useActions,
  useGetRunHistoryQuery,
  useGetUserRoundHistoryQuery,
  useTypedSelector,
} from '@root/store/store';
import type { LudoHistoryRow } from '@root/store/types';
import { useImageUrl } from '@shared/components/ImageContext';
import {
  historyQueryArgs,
  useHistoryState,
  type HistoryRange,
} from '@shared/history';
import { useAnalytics } from '@shared/hooks';
import { AnalyticsEvent } from '@shared/services/analytics';
import { useTranslation } from 'react-i18next';
import styles from './MyBetsHistoryModal.module.scss';
import {
  HistoryFiltersDialog,
  HistoryMessageDialog,
  matchesResult,
  periodLabel,
  resultLabel,
  RoundDetailDialog,
  RunHistoryPanel,
  type ResultFilter,
  type RunTab,
} from './parts';

// Rows arrive newest first, one per roll. Keep the ones we have already.
function merge(
  kept: LudoHistoryRow[],
  incoming: LudoHistoryRow[]
): LudoHistoryRow[] {
  const seen = new Set(kept.map((row) => row.id));
  const fresh = incoming.filter((row) => !seen.has(row.id));
  return fresh.length ? [...kept, ...fresh] : kept;
}

const MyBetsHistoryModal: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { balance } = useTypedSelector(balanceSelector);
  const { myBetsHistoryModalOpen } =
    useTypedSelector<AppSliceState>(appSelector);
  const { setMyBetsHistoryModalOpen } = useActions();
  const { trackPage } = useAnalytics();
  const { page, goToPage } = useHistoryState(myBetsHistoryModalOpen);
  // The design opens on the last week, not on everything.
  const [range, setRange] = useState<HistoryRange>('D7');
  const [result, setResult] = useState<ResultFilter>('ALL');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { img, imgProps } = useImageUrl();

  const [tab, setTab] = useState<RunTab>('completed');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rows, setRows] = useState<LudoHistoryRow[]>([]);
  const loadedFor = useRef('');

  // Returning null below does not unmount this, so history opens on
  // whichever run was last viewed unless the choice is cleared.
  useEffect(() => {
    setSelectedId(null);
  }, [myBetsHistoryModalOpen]);

  const { data, isFetching, isError, refetch } = useGetUserRoundHistoryQuery(
    user?.id && myBetsHistoryModalOpen
      ? historyQueryArgs({
          userId: user.id,
          gameId: VALID_GAME_ID,
          page,
          range,
        })
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  // A run can span pages, so group across every page loaded.
  const key = `${range}`;
  useEffect(() => {
    const items = data?.data?.items;
    if (!items) return;
    setRows((kept) => (loadedFor.current === key ? merge(kept, items) : items));
    loadedFor.current = key;
  }, [data, key]);

  const runs = useMemo(() => summariseRuns(rows), [rows]);
  const selected = runs.find((run) => run.journeyId === selectedId) ?? null;

  // Opening a run fetches all its rolls, so the breakdown is never short.
  // currentData, not data: data keeps the last run's rolls after the
  // selection clears, so the card would reopen on the previous run.
  const { currentData: runData } = useGetRunHistoryQuery(
    user?.id && selectedId
      ? { userId: user.id, journeyId: selectedId }
      : skipToken
  );
  const wholeRun = useMemo(
    () =>
      runData?.data?.length ? (summariseRuns(runData.data)[0] ?? null) : null,
    [runData]
  );

  // What the active tab and filter actually leave to show.
  const shown = useMemo(
    () =>
      runs.filter((run) => {
        const done = isRunComplete(run.result);
        if (tab !== 'completed') return !done;
        return done && matchesResult(run.result, result);
      }),
    [runs, tab, result]
  );
  const filtered = range !== 'D7' || result !== 'ALL';

  const totalPages = data?.data?.totalPages ?? 0;
  const canLoadMore = page + 1 < totalPages;

  const handleClose = () => {
    trackPage(AnalyticsEvent.HISTORY_CLOSED);
    setMyBetsHistoryModalOpen(false);
  };

  // Pages replaced scrolling; the event keeps its old name for the dashboards.
  const handleLoadMore = () => {
    trackPage(AnalyticsEvent.HISTORY_SCROLLED, { nextPage: page + 1 });
    goToPage(page + 1);
  };

  const openRun = (run: RunSummary) => setSelectedId(run.journeyId);

  // A new period is a new query, so start the paging over.
  const applyFilters = (next: HistoryRange, nextResult: ResultFilter) => {
    setRange(next);
    setResult(nextResult);
    setFiltersOpen(false);
    goToPage(0);
  };

  if (!myBetsHistoryModalOpen) return null;

  // Nothing to show yet, nothing to show at all, or nothing we could fetch.
  if (isFetching && rows.length === 0) {
    return (
      <HistoryMessageDialog
        headline={t('Loading your rounds…')}
        body={t('Please wait while we retrieve your recent activity.')}
        onClose={handleClose}
      />
    );
  }
  if (isError) {
    return (
      <HistoryMessageDialog
        headline={t('History is unavailable')}
        body={t('Please try again. Your current round is unaffected.')}
        action={{ label: t('RETRY'), onClick: refetch }}
        onClose={handleClose}
      />
    );
  }
  // A filter they can't see is one they can't clear, so offer the reset.
  if (shown.length === 0) {
    return (
      <HistoryMessageDialog
        headline={t('No rounds yet')}
        body={t('Your completed rounds will appear here after you play.')}
        action={
          filtered
            ? {
                label: t('RESET FILTERS'),
                onClick: () => applyFilters('D7', 'ALL'),
              }
            : { label: t('BACK TO GAME'), onClick: handleClose }
        }
        onClose={handleClose}
      />
    );
  }

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          {
            '--panel-skin': `url(${img('PopupFrame')})`,
          } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t('Player history')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={handleClose}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>
        <RunHistoryPanel
          runs={shown}
          symbol={balance.currencySymbol}
          tab={tab}
          onTabChange={setTab}
          onView={openRun}
          canLoadMore={canLoadMore}
          onLoadMore={handleLoadMore}
          primarySkin={img('ButtonSkin_Primary')}
          secondarySkin={img('ButtonSkin_Secondary')}
          periodLabel={t(periodLabel(range))}
          resultLabel={t(resultLabel(result))}
          onOpenFilters={() => setFiltersOpen(true)}
        />
      </div>
      <HistoryFiltersDialog
        open={filtersOpen}
        range={range}
        result={result}
        onApply={applyFilters}
        onClose={() => setFiltersOpen(false)}
      />
      <RoundDetailDialog
        run={wholeRun ?? selected}
        symbol={balance.currencySymbol}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
};

export default MyBetsHistoryModal;
